/**
 * 管理后台应用入口文件
 * 初始化Vue应用、插件、全局配置等
 */

import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import '@yuandezuohua/smart-workflow/style.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { VueQueryPlugin } from '@tanstack/vue-query'

import App from './App.vue'
import router from './router'
import { auth, initAuth } from '@/auth'
import { setupGlobalComponents } from './components'
import { setupGlobalDirectives } from './directives'
import { setupErrorHandler } from './utils/error-handler'
import { setupPermissionGuard } from './router/permission'
import PageLoading from '@/components/Loading/PageLoading.vue'
import { moduleLoader } from '@/core/module-loader'
import { usePermissionStore } from '@/stores/permission'
import { addDynamicRoutes } from '@/router/permission'

// 样式文件
import './styles/index.scss'

/**
 * 创建Vue应用实例
 */
function createVueApp() {
  const app = createApp(App)

  return app
}

/**
 * 配置应用插件
 */
async function setupPlugins(app: ReturnType<typeof createApp>) {
  // Pinia状态管理
  const pinia = createPinia()
  app.use(pinia)
  setActivePinia(pinia)

  // 初始化认证，恢复权限状态，确保后续路由过滤可用
  initAuth({})

  // 在安装路由之前，恢复用户状态并生成权限路由后再挂载
  try {
    const { useUserStore } = await import('./stores/user')
    const userStore = useUserStore()
    await userStore.initUserState()

    await moduleLoader.loadModules()

    const permissionStore = usePermissionStore()
    const accessRoutes = await permissionStore.generateRoutes()
    addDynamicRoutes(router, accessRoutes)
  } catch (e) {
    console.error('预加载模块路由失败:', e)
  }

  // Vue Router
  app.use(router)

  // Element Plus UI库
  app.use(ElementPlus, {
    locale: zhCn,
    size: 'default'
  })

  // TanStack Vue Query
  app.use(VueQueryPlugin)

  // 注册模块配置系统权限指令
  // 留给 setupGlobalDirectives 统一注册，避免重复
}

/**
 * 配置全局属性和方法
 */
function setupGlobalProperties(app: ReturnType<typeof createApp>) {
  // 全局属性
  app.config.globalProperties.$auth = auth.authManager

  // 开发环境下的调试工具
  if (import.meta.env.DEV) {
    app.config.globalProperties.$log = console.log
    app.config.globalProperties.$warn = console.warn
    app.config.globalProperties.$error = console.error
  }
}

/**
 * 应用初始化
 *
 * 变更说明：
 * - 用户登录状态检查和路由表生成逻辑已移至router.beforeEach中执行
 * - 此函数仅负责初始化基础功能
 */
async function initializeApp() {
  try {

    // 初始化权限管理
    initAuth({})

    // 动态导入用户状态管理store
    const { useUserStore } = await import('./stores/user')
    const userStore = useUserStore()

    // 初始化用户状态（从本地存储恢复登录状态）
    await userStore.initUserState()

    // 动态导入应用状态管理store
    const { useAppStore } = await import('./stores/app')
    const appStore = useAppStore()

    // 初始化应用设置（主题、布局等）
    appStore.initApp()

    // 初始化模块系统
    const { initializeModuleSystem } = await import('./services/module-init.service')
    await initializeModuleSystem()

    // 初始化模块配置系统权限
    const { useModulePermissionStore } = await import('./stores/module-permission')
    const modulePermissionStore = useModulePermissionStore()
    await modulePermissionStore.initialize()

  } catch (error) {
    console.error('初始化应用失败:', error)
  }
}

/**
 * 启动应用
 */
async function bootstrap() {
  try {
    // 创建应用实例
    const app = createVueApp()

    // 配置插件
    await setupPlugins(app)

    // 配置全局组件
    setupGlobalComponents(app)

    // 配置全局指令
    setupGlobalDirectives(app)

    // 配置全局属性
    setupGlobalProperties(app)

    // 配置错误处理
    setupErrorHandler(app)
    await initializeApp()

    // 配置路由权限守卫（在用户状态初始化完成后设置）
    setupPermissionGuard(router)

    // 显示加载组件
    const loadingApp = createApp(PageLoading)
    // 为Loading组件配置Element Plus插件
    loadingApp.use(ElementPlus, {
      locale: zhCn
    })
    loadingApp.mount('#app')

    // 等待一小段时间确保加载组件显示
    await new Promise(resolve => setTimeout(resolve, 100))

    // 卸载加载组件并挂载主应用
    loadingApp.unmount()
    app.mount('#app')
    ;(window as any).__APP_MOUNTED__ = true

    // 开发环境下的调试信息
    if (import.meta.env.DEV) {
      console.debug('[dev] bootstrap complete')
    }

  } catch (error) {
    console.error('❌ 应用启动失败:', error)

    // 显示错误页面
    const appEl = document.getElementById('app')
    if (appEl) {
      appEl.innerHTML = `
      <div style="
        height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: #f5f5f5;
        color: #666;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="font-size: 48px; margin-bottom: 16px;">💥</div>
        <div style="font-size: 18px; margin-bottom: 8px;">应用启动失败</div>
        <div style="font-size: 14px; color: #999; margin-bottom: 20px;">请检查网络连接或联系技术支持</div>
        <button 
          onclick="location.reload()" 
          style="
            padding: 8px 16px;
            background: #409eff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          "
        >
          重新加载
        </button>
      </div>
    `
    }
  }
}

// 启动应用
bootstrap()
